import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import { toast } from "sonner";
import {
  Stack, ChartLineUp, Scales, ClockCounterClockwise, FilePdf, ArrowRight, ArrowLeft,
  MicrosoftExcelLogo, CurrencyGbp, TrendUp, Receipt, Cube, Lock, PaperPlaneTilt, EnvelopeSimple, X,
} from "@phosphor-icons/react";

const TYPES = [
  { id: "ACCRUAL", slug: "accruals", label: "Accruals", icon: TrendUp, to: "/accruals", tone: "orange",
    blurb: "Costs incurred but not yet invoiced." },
  { id: "PREPAYMENT", slug: "prepayments", label: "Prepayments", icon: Receipt, to: "/prepayments", tone: "navy",
    blurb: "Costs paid in advance, released over time." },
  { id: "DEFERRED_INCOME", slug: "deferred-income", label: "Deferred Income", icon: CurrencyGbp, to: "/deferred-income", tone: "positive",
    blurb: "Income invoiced in advance, recognised over time." },
  { id: "DEPRECIATION", slug: "depreciation", label: "Depreciation", icon: Cube, to: "/fixed-assets", tone: "warn",
    blurb: "Fixed-asset NBV roll-forward and depreciation charge." },
];

const reportsFor = (t) => {
  const isDep = t.id === "DEPRECIATION";
  return [
    {
      id: `${t.slug}-register`, icon: Stack, title: `${t.label} Register`,
      desc: "Original, released and remaining balances for every schedule, with the next journal date and status.",
      to: t.to, pdf: `/register/pdf?status=All&type=${t.id}`, excel: `/register/excel?status=All&type=${t.id}`, file: `${t.slug}-register`,
      email: { report: "register", type: t.id },
    },
    {
      id: `${t.slug}-forecast`, icon: ChartLineUp,
      title: isDep ? "NBV Roll-forward (12-month)" : "12-Month Release Forecast",
      desc: isDep
        ? "Opening NBV, the depreciation charge each month and closing NBV over the next 12 months."
        : "Month-by-month projection of releases over the next 12 months, with opening and closing balances.",
      to: `/forecast?type=${t.id}`, pdf: `/forecast/pdf?type=${t.id}`, excel: `/forecast/excel?type=${t.id}`, file: `${t.slug}-forecast`,
      email: { report: "forecast", type: t.id },
    },
    {
      id: `${t.slug}-balance`, icon: Scales,
      title: isDep ? "NBV as at month-end" : "Balance as at month-end",
      desc: isDep
        ? "Net book value per asset at any selected month-end."
        : "Outstanding balance per item at any selected month-end.",
      to: `/forecast?type=${t.id}`, pdf: `/forecast/balance-at/pdf?type=${t.id}`, excel: `/forecast/balance-at/excel?type=${t.id}`, file: `${t.slug}-balance`,
      email: { report: "balance", type: t.id },
    },
    {
      id: `${t.slug}-movement`, icon: Scales, title: "Movement Report (Opening / Movement / Closing)",
      desc: "Per line item for any month or custom period: opening balance, movement in the period and closing balance, with subtotals per type. Choose the release schedule or posted-to-date basis.",
      to: `/movement?type=${t.id}`, noExport: true, file: `${t.slug}-movement`, hidden: isDep,
    },
    {
      id: `${t.slug}-recon`, icon: Scales, title: "Reconciliation / Balance Check",
      desc: "Compare the live Xero balance on each nominal code against the register, highlighting any variances.",
      to: `/reconciliation?type=${t.id}`, pdf: `/reconciliation/pdf?type=${t.id}`, excel: `/reconciliation/excel?type=${t.id}`, file: `${t.slug}-reconciliation`,
      email: { report: "reconciliation", type: t.id },
    },
    {
      id: `${t.slug}-activity`, icon: ClockCounterClockwise, title: "Activity Report",
      desc: "Audit trail of schedules, adjustments, reversals and posted journals for this category.",
      to: `/audit?type=${t.id}`, pdf: `/audit/pdf?type=${t.id}`, excel: `/audit/excel?type=${t.id}`, file: `${t.slug}-activity`,
    },
  ];
};

const TONE = {
  orange: "bg-orange/10 text-orangeink", navy: "bg-navy/[0.06] text-navy",
  positive: "bg-positive/10 text-positive", warn: "bg-warn/15 text-warn",
};

export default function Reports() {
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "" });
  const [orgName, setOrgName] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setContact(data.settings.client_contact || { name: "", email: "" });
      setOrgName(data.settings.active_org_name || "");
    }).catch(() => {});
  }, []);

  const download = async (path, file, ext) => {
    setBusy(`${file}-${ext}`);
    try {
      const res = await api.get(path, { responseType: "blob" });
      const mime = ext === "pdf" ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${file}-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate the file");
    } finally { setBusy(""); }
  };

  const sel = TYPES.find((t) => t.id === selected);

  return (
    <Page>
      <PageHeader overline="Reporting" title="Reports"
        subtitle="Pick a register to see every report for the active entity — each one locked to that type." />

      {sel ? (
        <TypeGroup type={sel} reports={reportsFor(sel)} busy={busy} download={download} onBack={() => setSelected(null)} onEmail={setEmailFor} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setSelected(t.id)} data-testid={`report-type-${t.slug}`}
                className="text-left bg-white border border-slate-200 rounded-2xl shadow-card p-6 hover:border-orange/60 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200 group">
                <div className={`w-12 h-12 rounded-[5px] flex items-center justify-center ${TONE[t.tone]}`}>
                  <Icon size={24} weight="fill" />
                </div>
                <h3 className="font-heading font-bold text-navy tracking-tight mt-4">{t.label}</h3>
                <p className="text-sm text-navy/70 mt-1 leading-snug">{t.blurb}</p>
                <span className="flex items-center gap-1.5 text-sm font-medium text-orangeink mt-4 group-hover:gap-2.5 transition-all">
                  View reports <ArrowRight size={15} />
                </span>
              </button>
            );
          })}
        </div>
      )}
      {emailFor && (
        <EmailReportModal report={emailFor} defaultEmail={contact.email} orgName={orgName} onClose={() => setEmailFor(null)} />
      )}
    </Page>
  );
}

function EmailReportModal({ report, defaultEmail, orgName, onClose }) {
  const [to, setTo] = useState(defaultEmail || "");
  const [pdf, setPdf] = useState(true);
  const [excel, setExcel] = useState(false);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!to.trim()) { toast.error("Enter a recipient email"); return; }
    const formats = [...(pdf ? ["pdf"] : []), ...(excel ? ["excel"] : [])];
    if (!formats.length) { toast.error("Choose at least one format"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/reports/email", {
        report: report.email.report, type: report.email.type, formats, to: to.trim(),
      });
      toast.success(`Report emailed to ${data.to}`);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the email");
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/40 backdrop-blur-sm p-4" data-testid="email-report-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading font-bold text-navy text-lg flex items-center gap-2">
            <EnvelopeSimple size={20} weight="fill" className="text-orangeink" /> Email report
          </h3>
          <button onClick={onClose} data-testid="email-modal-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
        </div>
        <p className="text-sm text-navy/70 mb-4">
          Sending <span className="font-semibold text-navy">{report.title}</span>{orgName ? <> for <span className="font-semibold text-navy">{orgName}</span></> : null}. The PDF is dual-branded with your practice &amp; the entity logo.
        </p>
        <label className="text-xs font-medium text-navy/70 mb-1 block">Recipient</label>
        <input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-recipient-input"
          placeholder="client@company.co.uk"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-navy focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none" />
        {!defaultEmail && <p className="text-xs text-warn mt-1.5">No saved client contact — set one under Entity Settings → Branding to pre-fill this.</p>}
        <div className="text-xs font-medium text-navy/70 mt-4 mb-2">Attach as</div>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 text-sm text-navy border border-slate-200 rounded-lg px-3 py-2 cursor-pointer flex-1" data-testid="email-format-pdf">
            <input type="checkbox" checked={pdf} onChange={(e) => setPdf(e.target.checked)} className="accent-orange w-4 h-4" />
            <FilePdf size={16} /> PDF
          </label>
          <label className="flex items-center gap-2 text-sm text-navy border border-slate-200 rounded-lg px-3 py-2 cursor-pointer flex-1" data-testid="email-format-excel">
            <input type="checkbox" checked={excel} onChange={(e) => setExcel(e.target.checked)} className="accent-positive w-4 h-4" />
            <MicrosoftExcelLogo size={16} /> Excel
          </label>
        </div>
        <button onClick={send} disabled={sending} data-testid="email-send-btn"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-orange text-white font-semibold px-6 py-2.5 rounded-xl hover:bg-navy transition-colors disabled:opacity-60">
          <PaperPlaneTilt size={18} weight="fill" /> {sending ? "Sending…" : "Send report"}
        </button>
      </div>
    </div>
  );
}

function TypeGroup({ type, reports, busy, download, onBack, onEmail }) {
  const Icon = type.icon;
  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className={`w-11 h-11 rounded-[5px] flex items-center justify-center ${TONE[type.tone]}`}>
            <Icon size={22} weight="fill" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="font-heading font-bold text-navy text-xl tracking-tight">{type.label} reports</h2>
              <span data-testid="type-lock-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-orangeink bg-orange/10 border border-orange/30 px-2.5 py-1 rounded-lg">
                <Lock size={12} weight="fill" /> Filter locked
              </span>
            </div>
            <p className="text-sm text-navy/70 mt-0.5">Every report below is pre-scoped to {type.label.toLowerCase()} only.</p>
          </div>
        </div>
        <button onClick={onBack} data-testid="reports-back-btn"
          className="flex items-center gap-2 bg-white border border-slate-200 text-navy px-4 py-2.5 rounded-lg text-sm font-medium hover:border-navy hover:bg-navy/5 transition-colors">
          <ArrowLeft size={16} /> All reports
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {reports.filter((r) => !r.hidden).map((r) => <ReportCard key={r.id} r={r} busy={busy} download={download} onEmail={onEmail} />)}
      </div>
    </>
  );
}

function ReportCard({ r, busy, download, onEmail }) {
  const Icon = r.icon;
  return (
    <div data-testid={`report-card-${r.id}`}
      className="bg-white border border-slate-200 rounded-2xl shadow-card p-6 flex flex-col hover:border-orange/50 hover:shadow-card-hover transition-all duration-200">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-lg bg-orange/10 text-orangeink flex items-center justify-center shrink-0">
          <Icon size={22} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-navy tracking-tight leading-tight">{r.title}</h3>
          <p className="text-sm text-navy/70 mt-1.5">{r.desc}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100">
        <Link to={r.to} data-testid={`report-open-${r.id}`}
          className="flex items-center gap-1.5 text-sm font-medium text-orangeink hover:gap-2.5 transition-all">
          Open report <ArrowRight size={15} />
        </Link>
        {!r.noExport && (
          <div className="ml-auto flex items-center gap-2">
            {r.email && onEmail && (
              <button onClick={() => onEmail(r)} data-testid={`report-email-${r.id}`}
                className="flex items-center gap-2 border border-orange/40 text-orangeink px-3.5 py-2 rounded-lg text-sm font-medium hover:bg-orange hover:text-white transition-colors duration-200"
                title="Email this report to the client contact">
                <EnvelopeSimple size={16} /> Email
              </button>
            )}
            <button onClick={() => download(r.pdf, r.file, "pdf")} disabled={busy === `${r.file}-pdf`} data-testid={`report-pdf-${r.id}`}
              className="flex items-center gap-2 border border-slate-200 text-navy px-3.5 py-2 rounded-lg text-sm font-medium hover:bg-navy hover:text-white transition-colors duration-200 disabled:opacity-60">
              <FilePdf size={16} /> {busy === `${r.file}-pdf` ? "…" : "PDF"}
            </button>
            <button onClick={() => download(r.excel, r.file, "xlsx")} disabled={busy === `${r.file}-xlsx`} data-testid={`report-excel-${r.id}`}
              className="flex items-center gap-2 border border-positive/40 text-positive px-3.5 py-2 rounded-lg text-sm font-medium hover:bg-positive hover:text-white transition-colors duration-200 disabled:opacity-60">
              <MicrosoftExcelLogo size={16} /> {busy === `${r.file}-xlsx` ? "…" : "Excel"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
